import { filter, isEmpty, sumBy, values, maxBy, map, orderBy } from 'lodash';
import { Record } from 'immutable';
import { MONTHLY } from 'commons-vehicle/PurchaseMethods/constants/paymentFrequencies.js';
import { DATA_TYPES } from 'commons-vehicle/constants/dataTypes.js';

const TermRecord = Record({
    term: 0,
    termIndex: 0,
    apr: 0,
    residualRate: 0,
    cashDown: 0,
    discounts: 0,
    rebates: 0,
    rebatesTaxes: 0,
    rebatesAtDelivery: 0,
    rebatesTaxesAtDelivery: 0,
    rebatesAtStandardRate: 0,
    paymentFrequencies: {},
    paymentFrequency: MONTHLY,
    payment: 0,
    paymentTaxes: 0,
    financedAmount: 0,
    financedAmountWithTaxes: 0,
    financedAmountAdjustment: 0,
    financedAmountWithTaxesAdjustment: 0,
    residualAmount: 0,
    optionsResidualRetail: 0,
    optionsPaymentAdjustment: 0,
    optionsPaymentTaxesAdjustment: 0,
    cashDownPaymentAdjustment: 0,
    netTradeInPaymentAdjustment: 0,
    netTradeInPaymentTaxesAdjustment: 0,
    calculatedNetPayment: 0,
    calculatedPayment: 0,
    includedItemsCodes: [],
    rebatesDetails: [],
});

class Term extends TermRecord {
    calculatePaymentAdjustment(financedAmount, residualRetailAmount) {
        const currentPaymentFrequency = this.paymentFrequencies[this.paymentFrequency];
        const residualAmount = residualRetailAmount * (this.residualRate / 100);

        return currentPaymentFrequency
            ? financedAmount * currentPaymentFrequency.financeFactor +
                  residualAmount * currentPaymentFrequency.balloonFactor
            : 0;
    }

    getBestPaymentFrequency() {
        if (isEmpty(this.paymentFrequencies)) {
            return MONTHLY;
        }
        const frequencies = values(this.paymentFrequencies);
        return maxBy(frequencies, 'paymentFrequency').paymentFrequency;
    }

    getAvailablePaymentFrequencies() {
        if (isEmpty(this.paymentFrequencies)) {
            return [MONTHLY];
        }
        const frequencies = values(this.paymentFrequencies);
        return orderBy(map(frequencies, (frequency) => frequency.paymentFrequency), ['paymentFrequency'], ['desc']);
    }

    getPaymentFrequency() {
        return this.paymentFrequencies[this.paymentFrequency];
    }

    getCalculatedPayments(taxesIncluded) {
        const calculatedPaymentRounded = Math.round(this.calculatedPayment * 100) / 100;
        const calculatedPaymentTaxes =
            Math.round(
                (this.paymentTaxes + this.optionsPaymentTaxesAdjustment - this.netTradeInPaymentTaxesAdjustment) * 100
            ) / 100;
        const calculatedPaymentBeforeTaxes = taxesIncluded
            ? calculatedPaymentRounded - calculatedPaymentTaxes
            : calculatedPaymentRounded;

        return {
            calculatedPaymentBeforeTaxes,
            calculatedPaymentTaxes,
            calculatedPayment: calculatedPaymentBeforeTaxes + calculatedPaymentTaxes,
        };
    }

    getFinancedAmount() {
        return this.financedAmount;
    }

    getDiscountsAndRebates() {
        return this.discounts + this.rebates;
    }

    getDisclaimerAmounts(taxesIncluded, financedAmountAdjustment, residualAmountAdjustment) {
        const calculatedPayments = this.getCalculatedPayments(false);
        const paymentsCount = (this.term * this.paymentFrequency) / MONTHLY;
        const financedAmount =
            (taxesIncluded
                ? this.financedAmountWithTaxes + this.financedAmountWithTaxesAdjustment
                : this.financedAmount + this.financedAmountAdjustment) + financedAmountAdjustment;
        const residualAmount = this.residualAmount + residualAmountAdjustment;

        const totalObligation =
            (taxesIncluded ? calculatedPayments.calculatedPayment : calculatedPayments.calculatedPaymentBeforeTaxes) *
                paymentsCount +
            residualAmount;
        const costOfCredit = totalObligation < financedAmount ? 0 : totalObligation - financedAmount;

        return {
            totalObligation,
            costOfCredit,
            residualAmount,
        };
    }

    getElectricRebate() {
        const electricRebates = filter(this.rebatesDetails, (rebateDetails) => {
            return rebateDetails.typeId === DATA_TYPES.REBATE.TYPES.ELECTRIC;
        });

        if (isEmpty(electricRebates)) {
            return null;
        }

        return {
            description: electricRebates[0].description,
            rebate: sumBy(electricRebates, (electricRebate) => electricRebate.amount),
        };
    }

    getEmployeeRebate() {
        const employeeRebates = filter(
            this.rebatesDetails,
            (rebateDetails) => rebateDetails.typeId === DATA_TYPES.REBATE.TYPES.EMPLOYEE
        );

        if (isEmpty(employeeRebates)) {
            return null;
        }

        return {
            description: employeeRebates[0].description,
            rebate: sumBy(employeeRebates, (employeeRebate) => employeeRebate.amount),
        };
    }
}

export default Term;



// WEBPACK FOOTER //
// ./src/PurchaseMethods/models/TermModel.js